import {
	Autocomplete,
	Box,
	Button,
	Divider,
	Grid,
	IconButton,
	Paper,
	TextField,
	Typography,
	useTheme,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useBlocker, useNavigate, useOutletContext } from "react-router-dom";
import {
	DeleteOutlineOutlined,
	PeopleOutlineOutlined,
} from "@mui/icons-material";
import WorkspacesOutlinedIcon from "@mui/icons-material/WorkspacesOutlined";
import { LoadingButton } from "@mui/lab";

import api from "../../../services/api";
import useStyles from "./styles";
import { maskCnpj } from "../../../utils/masks";
import InternoModalConfirmacaoSaida from "../../../components/modalSaindoDaPagina";
import GrupoChip from "../../../components/grupoEconomico/GrupoChip";
import { getClienteOptionLabel } from "../../../components/grupoEconomico/helpers";
import TagCliente from "../../../components/tagCliente";

export default function GrupoEconomicoPerfil() {
	const outletContext = useOutletContext() || [];
	const setSnackbar = outletContext[0] || (() => {});
	const clienteInfo = outletContext[1];
	const setClienteInfo = outletContext[2] || (() => {});
	const theme = useTheme();
	const styles = useStyles(theme);
	const navigate = useNavigate();

	const [editMode, setEditMode] = useState(false);
	const [changed, setChanged] = useState(false);
	const [nome, setNome] = useState(
		clienteInfo?.nomeFantasia || clienteInfo?.razaoSocial || "",
	);
	const [membros, setMembros] = useState(clienteInfo?.membros || []);
	const [clientes, setClientes] = useState([]);
	const [novoMembro, setNovoMembro] = useState(null);
	const [loadingSave, setLoadingSave] = useState(false);
	const [loadingDelete, setLoadingDelete] = useState(false);
	const [openConfirmDelete, setOpenConfirmDelete] = useState(false);
	const [confirmacaoSaidaPagina, setConfirmacaoSaidaPagina] = useState(false);

	const isAdmin = useMemo(() => {
		const result = JSON.parse(localStorage.getItem("user"));
		if (Array.isArray(result?.usuario?.rlPermissoesUsuario)) {
			return result.usuario.rlPermissoesUsuario.some(
				(permissao) =>
					permissao?.permissao?.codigoPermissao?.toUpperCase() === "ADM",
			);
		}
		return false;
	}, []);

	const blocker = useBlocker(({ currentLocation, nextLocation }) => {
		return (
			currentLocation.pathname !== nextLocation.pathname &&
			!!editMode &&
			!!changed
		);
	});

	useEffect(() => {
		if (blocker?.state === "blocked") {
			setConfirmacaoSaidaPagina(true);
		}
	}, [blocker?.state]);

	// Sincroniza o estado local quando o grupo selecionado muda
	useEffect(() => {
		setNome(clienteInfo?.nomeFantasia || clienteInfo?.razaoSocial || "");
		setMembros(clienteInfo?.membros || []);
		setEditMode(false);
		setChanged(false);
		setNovoMembro(null);
	}, [clienteInfo?.id]);

	// Lista de clientes para adicionar membros (só quando entrar em edição)
	useEffect(() => {
		if (!editMode || clientes.length > 0) return;
		const getClientes = async () => {
			try {
				const response = await api.get("/getClients");
				setClientes(response?.data || []);
			} catch (error) {
				console.error(error);
			}
		};
		getClientes();
	}, [editMode]);

	// Elegíveis: não é grupo, não pertence a outro grupo e ainda não está na lista
	const clientesElegiveis = useMemo(() => {
		const membrosIds = new Set(membros.map((membro) => membro?.id));
		return clientes.filter(
			(cliente) =>
				!cliente?.isGrupoEconomico &&
				(!cliente?.grupoEconomicoId ||
					cliente?.grupoEconomicoId === clienteInfo?.id) &&
				!membrosIds.has(cliente?.id),
		);
	}, [clientes, membros, clienteInfo?.id]);

	const handleCancelar = () => {
		setNome(clienteInfo?.nomeFantasia || clienteInfo?.razaoSocial || "");
		setMembros(clienteInfo?.membros || []);
		setNovoMembro(null);
		setEditMode(false);
		setChanged(false);
	};

	const handleAddMembro = () => {
		if (!novoMembro) return;
		setMembros((prev) => [...prev, novoMembro]);
		setNovoMembro(null);
		setChanged(true);
	};

	const handleRemoveMembro = (clienteId) => {
		setMembros((prev) => prev.filter((membro) => membro?.id !== clienteId));
		setChanged(true);
	};

	const handleSalvar = async () => {
		if (!nome?.trim()) {
			setSnackbar({
				children: "Informe o nome do grupo",
				severity: "error",
			});
			return;
		}
		if (!membros?.length) {
			setSnackbar({
				children: "O grupo deve conter ao menos um cliente membro",
				severity: "error",
			});
			return;
		}

		try {
			setLoadingSave(true);
			await api.put("/interno/update/grupoEconomico", {
				grupoId: clienteInfo?.id,
				nome: nome.trim(),
				membrosIds: membros.map((membro) => membro.id),
			});
			setClienteInfo({
				...clienteInfo,
				razaoSocial: nome.trim(),
				nomeFantasia: nome.trim(),
				membros,
			});
			setSnackbar({
				children: "Grupo econômico atualizado com sucesso",
				severity: "success",
			});
			setEditMode(false);
			setChanged(false);
		} catch (error) {
			console.error(error);
			setSnackbar({
				children:
					error?.response?.data?.error ||
					error?.response?.data?.message ||
					"Erro ao atualizar o grupo econômico",
				severity: "error",
			});
		} finally {
			setLoadingSave(false);
		}
	};

	const handleExcluirGrupo = async () => {
		try {
			setLoadingDelete(true);
			await api.post("/interno/delete/grupoEconomico", {
				grupoId: clienteInfo?.id,
			});
			setSnackbar({
				children: "Grupo econômico excluído. Os clientes membros foram mantidos.",
				severity: "success",
			});
			localStorage.removeItem("filterData");
			setClienteInfo(null);
			navigate("/home/perfil");
		} catch (error) {
			console.error(error);
			setSnackbar({
				children:
					error?.response?.data?.error ||
					"Erro ao excluir o grupo econômico",
				severity: "error",
			});
		} finally {
			setLoadingDelete(false);
			setOpenConfirmDelete(false);
		}
	};

	return (
		<Box sx={styles.container}>
			<Paper
				sx={styles.containerPaper}
				elevation={theme.palette.mode === "dark" ? 0 : 3}
			>
				<Box
					sx={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "center",
						flexWrap: "wrap",
						gap: 1,
						mb: 2,
					}}
				>
					<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
						<WorkspacesOutlinedIcon sx={{ color: theme.palette.primary.main }} />
						<Typography variant="h6" fontWeight={600}>
							{clienteInfo?.nomeFantasia || clienteInfo?.razaoSocial}
						</Typography>
						<GrupoChip />
					</Box>
					{isAdmin && (
						<Box sx={{ display: "flex", gap: 1 }}>
							{editMode ? (
								<>
									<Button size="small" onClick={handleCancelar}>
										Cancelar
									</Button>
									<LoadingButton
										size="small"
										variant="contained"
										loading={loadingSave}
										onClick={handleSalvar}
									>
										Salvar
									</LoadingButton>
								</>
							) : (
								<>
									<Button
										size="small"
										color="error"
										variant="outlined"
										onClick={() => setOpenConfirmDelete(true)}
									>
										Excluir grupo
									</Button>
									<Button
										size="small"
										variant="contained"
										onClick={() => setEditMode(true)}
									>
										Editar
									</Button>
								</>
							)}
						</Box>
					)}
				</Box>
				<Divider sx={{ mb: 2 }} />

				<Grid container spacing={2}>
					<Grid item xs={12} md={6}>
						<TextField
							label="Nome do grupo"
							size="small"
							fullWidth
							disabled={!editMode}
							value={nome}
							onChange={(e) => {
								setNome(e.target.value);
								setChanged(true);
							}}
						/>
					</Grid>

					<Grid item xs={12}>
						<Box sx={styles.boxSubTitle}>
							<PeopleOutlineOutlined
								sx={{ color: theme.palette.primary.main }}
							/>
							<Typography variant="subtitle1" fontWeight={600}>
								{`Clientes do grupo (${membros?.length || 0})`}
							</Typography>
						</Box>
					</Grid>

					{membros?.map((membro) => (
						<Grid item xs={12} md={6} sm={12} key={membro?.id}>
							<Paper variant="outlined" sx={styles.paperSearchCardCliente}>
								<Box sx={styles.containerDataSearch}>
									<Box sx={styles.boxDataCard}>
										<Typography sx={{ ...styles.titleCard }}>
											Cliente: {membro?.nomeFantasia || membro?.razaoSocial}
										</Typography>
									</Box>
									<Box sx={styles.boxDataCard}>
										<Typography sx={styles.subDataCard}>
											CNPJ: {maskCnpj(membro?.cnpj) || membro?.cnpj || "-"}
										</Typography>
									</Box>
									<Box sx={styles.boxDataCard}>
										<Typography sx={styles.subDataCard}>
											Código cliente: {membro?.clienteErpId || "-"}
										</Typography>
									</Box>
								</Box>
								{editMode && isAdmin && (
									<Box sx={styles.containerButtonSearch}>
										<IconButton onClick={() => handleRemoveMembro(membro?.id)}>
											<DeleteOutlineOutlined color="error" />
										</IconButton>
									</Box>
								)}
							</Paper>
						</Grid>
					))}

					{editMode && isAdmin && (
						<Grid item xs={12} md={6} sm={12}>
							<Paper variant="outlined" sx={styles.paperSearchCliente}>
								<Box sx={{ display: "flex", gap: 1, p: 1 }}>
									<Autocomplete
										size="small"
										fullWidth
										options={clientesElegiveis}
										value={novoMembro}
										onChange={(_, newValue) => setNovoMembro(newValue)}
										getOptionLabel={(option) =>
											getClienteOptionLabel(option)
										}
										// Opcao com o produto do cliente como chip, igual a tabela de clientes
										renderOption={(props, option) => (
											<li {...props}>
												<Box sx={styles.opcaoCliente}>
													{getClienteOptionLabel(option)}
													{option?.tagsCliente?.map((tag) => (
														<TagCliente key={tag?.id} tag={tag} compacto />
													))}
												</Box>
											</li>
										)}
										renderInput={(params) => (
											<TextField
												{...params}
												label="Adicionar cliente ao grupo"
												size="small"
											/>
										)}
									/>
									<Button
										size="small"
										variant="contained"
										disabled={!novoMembro}
										onClick={handleAddMembro}
									>
										Adicionar
									</Button>
								</Box>
							</Paper>
						</Grid>
					)}
				</Grid>
			</Paper>

			<InternoModalConfirmacaoSaida
				openConfirmacao={openConfirmDelete}
				confirmLoad={loadingDelete}
				setOpenConfirmacao={setOpenConfirmDelete}
				execute={handleExcluirGrupo}
				severity={"error"}
				title={"Atenção"}
				contentText={
					"Deseja excluir o grupo econômico? Os clientes membros não serão excluídos, apenas o agrupamento."
				}
				confirmText={"Excluir"}
			/>

			<InternoModalConfirmacaoSaida
				openConfirmacao={confirmacaoSaidaPagina}
				setOpenConfirmacao={setConfirmacaoSaidaPagina}
				execute={() => {
					setConfirmacaoSaidaPagina(false);
					setChanged(false);
					setEditMode(false);
					if (blocker?.proceed) {
						blocker.proceed();
					}
				}}
				cancel={() => {
					setConfirmacaoSaidaPagina(false);
					if (blocker?.reset) {
						blocker.reset();
					}
				}}
				severity={"warning"}
				title={"Atenção"}
				contentText={
					"Existem alterações não salvas. Deseja sair sem salvar?"
				}
				confirmText={"Sair"}
			/>
		</Box>
	);
}
